import React from "react";
import { Badge, Tooltip, TooltipEnums } from "kubra-ux-forge";
import { useTranslation } from "react-i18next";
import {
  COMMON_NAMESPACE,
  PAYABLE_REVIEW_NAMESPACE,
} from "constants/i18n-namespaces";
import {
  getBadgeStyle,
  getLabel,
  mappingPaymentStatus,
  upperCaseFirstLetterInString,
} from "utilities/helper";
import { useSelector } from "react-redux";
import { getLabels } from "selectors/labels";
import {
  getPaymentIntent,
  getPaymentMethodLabel,
} from "selectors/paymentIntent";
import { useCurrencyFormatter, useDateFormatter } from "hooks";
import { AccountPayable, InvoicePayable } from "types/payable";
import { getPayableSession } from "selectors/payableSession";
import google_pay_mark from "assets/google-pay-mark.png";
import apple_pay_mark from "assets/apple-pay-mark.svg";
import paypal_mark from "assets/paypal-mark.png";
import FundingOption from "constants/FundingOption";

export const PaymentSuccessSummary = () => {
  const { t } = useTranslation(PAYABLE_REVIEW_NAMESPACE);
  const labelSource = useSelector(getLabels);

  const payableSession = useSelector(getPayableSession);
  const paymentIntentState = useSelector(getPaymentIntent);
  const selectedPayableType = payableSession?.payableType;
  const selectedPayable = payableSession?.payable;

  const selectedCharities = paymentIntentState.charities?.filter(
    (char) => char.selected
  );
  let charitiesTotal = 0.0;
  if (
    paymentIntentState.charities != null &&
    paymentIntentState.charities.length > 0
  ) {
    charitiesTotal = paymentIntentState.charities
      .filter((ch) => ch.selected)
      .reduce((charitiesTotal, charity) => charitiesTotal + charity.amount, 0);
  }

  const paymentMethodLabel = useSelector((state) =>
    getPaymentMethodLabel(state, t)
  );

  const dateFormatter = useDateFormatter();
  const currencyFormatter = useCurrencyFormatter();

  if (!payableSession?.payableItemResponse) {
    throw new Error("Prepare endpoint response doesn't exist in the session");
  }

  if (!payableSession?.payable) {
    throw new Error("Payable not available in the session");
  }

  return (
    <div data-cy="ez-pay-summary-container">
      <div
        className="ez-pay-summary-field-item"
        data-cy="ez-pay-summary-field-item-item"
      >
        <b>{t("item")}</b>
        {getLabel(labelSource.labels, selectedPayableType?.name)}
      </div>

      {selectedPayableType?.category.toLowerCase() === "invoice" ? (
        <div
          className="ez-pay-summary-field-item"
          data-cy="ez-pay-summary-field-item-invoice-number"
        >
          <b>
            {t("invoice-number", {
              invoiceName: getLabel(
                labelSource.labels,
                selectedPayableType?.name
              ),
            })}
          </b>
          {(selectedPayable as InvoicePayable).invoiceNumber}
        </div>
      ) : null}

      {selectedPayableType?.category.toLowerCase() === "account" ? (
        <div
          className="ez-pay-summary-field-item"
          data-cy="ez-pay-summary-field-item-account-number"
        >
          <b>{t("account-number")}</b>
          {(selectedPayable as AccountPayable).accountNumber}
        </div>
      ) : null}

      {paymentIntentState.paymentId && (
        <div
          className="ez-pay-summary-field-item"
          data-cy="ez-pay-summary-field-item-payment-id"
        >
          <b>{t("confirmation-number")}</b>
          {paymentIntentState.paymentId}
        </div>
      )}

      {paymentIntentState.paymentStatus && (
        <div
          className="ez-pay-summary-field-item"
          data-cy="ez-pay-summary-field-item-payment-status"
        >
          <b>{t("status")}</b>
          <Badge
            text={upperCaseFirstLetterInString(
              t(mappingPaymentStatus(paymentIntentState.paymentStatus), {
                ns: COMMON_NAMESPACE,
              })
            )}
            variant={getBadgeStyle(paymentIntentState.paymentStatus)}
          />
        </div>
      )}

      <div
        className="ez-pay-summary-field-item"
        data-cy="ez-pay-summary-field-item-payment-date"
      >
        <b>{t("payment-date")}</b>
        {dateFormatter.shortIsoFormat(paymentIntentState.paymentDate)}
      </div>

      <div
        className="ez-pay-summary-field-item"
        data-cy="ez-pay-summary-field-item-payment-method"
      >
        <b>{t("payment-method")}</b>
        {paymentIntentState.paymentMethod == FundingOption.ApplePay && (
          <img
            src={apple_pay_mark}
            alt="Apple Pay"
            style={{ height: "30px" }}
          ></img>
        )}
        {paymentIntentState.paymentMethod == FundingOption.GooglePay && (
          <img
            src={google_pay_mark}
            alt="Google Pay"
            style={{ height: "30px" }}
          ></img>
        )}
        {paymentIntentState.paymentMethod == FundingOption.PayPal && (
          <img src={paypal_mark} alt="PayPal"></img>
        )}
        {paymentMethodLabel}
      </div>

      <div
        className="ez-pay-summary-field-item"
        data-cy="ez-pay-summary-field-item-payment-amount"
      >
        <b>{t("payment-amount")}</b>
        {currencyFormatter.format(
          payableSession?.payableItemResponse.paymentAmount
        )}
      </div>

      {selectedCharities != undefined &&
        selectedCharities.length > 0 &&
        selectedCharities.map(
          (charityState) =>
            charityState.selected &&
            charityState.amount > 0 && (
              <div
                className="ez-pay-summary-field-item"
                key={charityState.charity.code}
              >
                <b>{t("donation-to", { name: charityState.charity.name })}</b>

                {currencyFormatter.format(charityState.amount)}
              </div>
            )
        )}

      <div
        className="ez-pay-summary-field-item"
        data-cy="ez-pay-summary-field-item-service-fee"
      >
        <b>
          {t("service-fee")}&nbsp;
          <Tooltip
            direction={TooltipEnums.TopRight}
            content={t("service-fee-tooltip")}
            data-cy="service-fee-tooltip"
          />
        </b>{" "}
        {currencyFormatter.format(
          payableSession?.payableItemResponse.fee.amount
        )}
      </div>

      <div
        className="ez-pay-summary-field-item payment-total"
        data-cy="ez-pay-field-item-payment-total"
      >
        <b>{t("payment-total")}</b>
        <b>
          {currencyFormatter.format(
            payableSession?.payableItemResponse.itemAndFeeTotalAmount
          )}
        </b>
      </div>
    </div>
  );
};
